import type { LucideIcon } from 'lucide-react';
import {
  BadgeDollarSign,
  Briefcase,
  Building2,
  Cloud,
  DollarSign,
  Factory,
  GraduationCap,
  Heart,
  Home,
  Leaf,
  Music,
  PenLine,
  Scale,
  ShoppingCart,
  Smartphone,
  TrendingUp,
  Truck,
  Users,
  Utensils,
  Zap,
} from 'lucide-react';

export type IndustryEnum =
  | 'b2b_saas'
  | 'consumer_tech'
  | 'fintech'
  | 'healthcare_biotech'
  | 'ecommerce_retail'
  | 'consulting'
  | 'marketing_agency'
  | 'manufacturing'
  | 'education_edtech'
  | 'media_entertainment'
  | 'real_estate'
  | 'energy_cleantech'
  | 'logistics'
  | 'government_nonprofit'
  | 'food_hospitality'
  | 'agriculture'
  | 'legal'
  | 'hr_recruiting'
  | 'sales'
  | 'other';

export interface NicheOption {
  id: IndustryEnum;
  label: string;
  icon: LucideIcon;
  description: string;
}

/**
 * Library niches — ids must match the backend IndustryEnum exactly.
 * The `id` field is the value sent to the API.
 */
export const NICHE_OPTIONS: NicheOption[] = [
  {
    id: 'b2b_saas',
    label: 'B2B SaaS',
    icon: Cloud,
    description: 'Software-as-a-service for businesses',
  },
  {
    id: 'consumer_tech',
    label: 'Consumer Tech',
    icon: Smartphone,
    description: 'Consumer-facing technology products',
  },
  {
    id: 'fintech',
    label: 'Fintech',
    icon: DollarSign,
    description: 'Financial technology and services',
  },
  {
    id: 'healthcare_biotech',
    label: 'Healthcare & Biotech',
    icon: Heart,
    description: 'Healthcare, biotech, and life sciences',
  },
  {
    id: 'ecommerce_retail',
    label: 'E-Commerce & Retail',
    icon: ShoppingCart,
    description: 'Online and offline retail',
  },
  {
    id: 'consulting',
    label: 'Consulting',
    icon: Briefcase,
    description: 'Business and management consulting',
  },
  {
    id: 'marketing_agency',
    label: 'Marketing Agency',
    icon: TrendingUp,
    description: 'Marketing, advertising, and PR agencies',
  },
  {
    id: 'manufacturing',
    label: 'Manufacturing',
    icon: Factory,
    description: 'Industrial and product manufacturing',
  },
  {
    id: 'education_edtech',
    label: 'Education & EdTech',
    icon: GraduationCap,
    description: 'Education and learning technology',
  },
  {
    id: 'media_entertainment',
    label: 'Media & Entertainment',
    icon: Music,
    description: 'Media, film, music, and entertainment',
  },
  {
    id: 'real_estate',
    label: 'Real Estate',
    icon: Home,
    description: 'Property and real estate',
  },
  {
    id: 'energy_cleantech',
    label: 'Energy & CleanTech',
    icon: Zap,
    description: 'Energy, renewables, and clean technology',
  },
  {
    id: 'logistics',
    label: 'Logistics',
    icon: Truck,
    description: 'Supply chain and logistics',
  },
  {
    id: 'government_nonprofit',
    label: 'Government & Nonprofit',
    icon: Building2,
    description: 'Public sector and nonprofit organizations',
  },
  {
    id: 'food_hospitality',
    label: 'Food & Hospitality',
    icon: Utensils,
    description: 'Food service, restaurants, and hospitality',
  },
  {
    id: 'agriculture',
    label: 'Agriculture',
    icon: Leaf,
    description: 'Farming and agricultural technology',
  },
  {
    id: 'legal',
    label: 'Legal',
    icon: Scale,
    description: 'Legal services and law firms',
  },
  {
    id: 'hr_recruiting',
    label: 'HR & Recruiting',
    icon: Users,
    description: 'Human resources and talent acquisition',
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: BadgeDollarSign,
    description: 'Sales, business development, and revenue',
  },
];

/** The "Other" card opens a text input; `other` itself is never stored. */
export const OTHER_NICHE_ID = 'other';

/** Mirrors the backend cap: custom text reaches AI prompts, so it stays short. */
export const NICHE_MAX_LENGTH = 100;

export const OTHER_NICHE_OPTION: NicheOption = {
  id: OTHER_NICHE_ID,
  label: 'Other',
  icon: PenLine,
  description: 'Type your own niche',
};

/** Library niche ids. Anything else in a selection is the user's own text. */
export const VALID_NICHE_IDS: ReadonlySet<string> = new Set(NICHE_OPTIONS.map(o => o.id));

export function nicheLabel(id: string): string {
  return NICHE_OPTIONS.find(o => o.id === id)?.label ?? id;
}

/** Trimmed custom niche, or null when blank or over {@link NICHE_MAX_LENGTH}. */
export function normalizeCustomNiche(raw: string): string | null {
  const value = raw.trim();
  return value && value.length <= NICHE_MAX_LENGTH ? value : null;
}
